import { Typography } from '@mui/material';
import UPCheckbox from 'components/app-container/checkbox-with-label';
import UPButton from 'components/button';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPInput from 'components/input';
import { CompanyFormat } from 'constants/CompanyFormat';
import { OnboardingCompanyStatus } from 'constants/OnboardingEnums';
import { CompanyStepFieldNames, OnboardingFlowCompanyStepFields } from 'modules/onboarding/types';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { sendOnboardingCompanyStepAction } from 'store/actions/company-onboarding-actions';
import {
  COMPANY_INFORMATION__COMMERCIAL_NAME,
  COMPANY_INFORMATION__COMPANY_FORMAT,
  COMPANY_INFORMATION__COMPANY_ID_NUMBER,
  COMPANY_INFORMATION__COMPANY_NAME,
  ONBOARDING__COMMERCIAL_NAME_PLACEHOLDER,
  ONBOARDING__COMPANY_NAME_PLACEHOLDER,
  ONBOARDING__DIFFERENT_COMMERCIAL_NAME,
  ONBOARDING__NEXT_STEP,
  ONBOARDING__PROCESS_ALREADY_FINISHED,
} from 'translations/constants';
import { getInputErrorText, getUpDropdownItemsFromEnum } from 'utils/helpers';
import { OnboardingFlowContext } from '../OnboardingFlow';
import {
  OnboardingCardError,
  OnboardingFlowCard,
  OnboardingFlowCardActionsContainer,
  OnboardingFlowCardContentContainer,
  OnboardingFlowCardContentRow,
  OnboardingFlowCardField,
  OnboardingFlowStepContainer,
} from './styles';

const FormDefaultValues: OnboardingFlowCompanyStepFields = {
  name: '',
  differentCommercialName: false,
  commercialName: '',
  companyIdNumber: null,
  companyFormat: '',
};

const CompanyStep: React.FC = () => {
  const {
    t,
    activeStep,
    onClickNextStep,
    onboardingData,
    setOnboardingData,
    setModalOpen,
    companyIdNumber,
    setCompanyIdNumber,
  } = useContext(OnboardingFlowContext);
  const companyFormatOptions: UPDropdownItem[] = getUpDropdownItemsFromEnum(CompanyFormat);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [processFinishedError, setProcessFinishedError] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OnboardingFlowCompanyStepFields>({
    defaultValues: onboardingData?.company || FormDefaultValues,
  });

  const differentCommercialNameWatch = watch(CompanyStepFieldNames.DIFFERENT_COMMERCIAL_NAME);

  const handleResponse = async (dataStatus: OnboardingCompanyStatus, data: OnboardingFlowCompanyStepFields) => {
    switch (dataStatus) {
      case OnboardingCompanyStatus.ONBOARDING_PROCESS_STARTED: {
        setModalOpen(true);
        setCompanyIdNumber(data.companyIdNumber);
        break;
      }
      case OnboardingCompanyStatus.ONBOARDING_PROCESS_FINISHED: {
        setProcessFinishedError(true);
        break;
      }
      case OnboardingCompanyStatus.CAN_START_ONBOARDING:
        setOnboardingData({
          ...onboardingData,
          company: data,
        });
        setCompanyIdNumber(data.companyIdNumber);
        onClickNextStep();
        break;
      default:
        break;
    }
  };

  const onProceed: SubmitHandler<OnboardingFlowCompanyStepFields> = async data => {
    setLoading(true);
    setProcessFinishedError(false);
    await dispatch(sendOnboardingCompanyStepAction({ data, companyIdNumber })).then(async response => {
      if (!companyIdNumber || data.companyIdNumber !== companyIdNumber) {
        await handleResponse(response.payload as OnboardingCompanyStatus, data);
      } else {
        setOnboardingData({
          ...onboardingData,
          company: data,
        });
        onClickNextStep();
      }
    });
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const onSubmit = () => {
    handleSubmit(onProceed)();
  };

  useEffect(() => {
    if (onboardingData?.company) {
      reset(onboardingData.company);
    }
  }, [onboardingData, reset]);

  return (
    <OnboardingFlowStepContainer activeStep={activeStep}>
      <OnboardingFlowCard>
        <OnboardingFlowCardContentContainer>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={6}>
              <Controller
                name={CompanyStepFieldNames.NAME}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__COMPANY_NAME)}
                    value={field.value}
                    placeholder={t(ONBOARDING__COMPANY_NAME_PLACEHOLDER)}
                    onChange={field.onChange}
                    required
                    error={!!errors.name}
                    helperText={getInputErrorText(errors, CompanyStepFieldNames.NAME)}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={6}>
              <Controller
                name={CompanyStepFieldNames.COMMERCIAL_NAME}
                control={control}
                rules={{ required: differentCommercialNameWatch }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__COMMERCIAL_NAME)}
                    value={field.value}
                    placeholder={t(ONBOARDING__COMMERCIAL_NAME_PLACEHOLDER)}
                    onChange={field.onChange}
                    disabled={!differentCommercialNameWatch}
                    required={differentCommercialNameWatch}
                    error={!!errors.commercialName}
                    helperText={getInputErrorText(errors, CompanyStepFieldNames.COMMERCIAL_NAME)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={4} display="flex" alignItems="flex-end">
              <Controller
                name={CompanyStepFieldNames.DIFFERENT_COMMERCIAL_NAME}
                control={control}
                render={({ field }) => (
                  <UPCheckbox
                    label={<Typography fontWeight="bold">{t(ONBOARDING__DIFFERENT_COMMERCIAL_NAME)}</Typography>}
                    value={field.value}
                    handleChange={field.onChange}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={6}>
              <Controller
                name={CompanyStepFieldNames.COMPANY_ID_NUMBER}
                control={control}
                rules={{
                  required: true,
                  maxLength: 10,
                  pattern: /^(0\d{0,9}|1\d{0,9}|\d{1,9})$/,
                }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__COMPANY_ID_NUMBER)}
                    value={field.value}
                    placeholder="vb. 0123456789"
                    onChange={field.onChange}
                    required
                    error={!!errors.companyIdNumber}
                    helperText={getInputErrorText(errors, CompanyStepFieldNames.COMPANY_ID_NUMBER)}
                    type="text"
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={4}>
              <Controller
                name={CompanyStepFieldNames.COMPANY_FORMAT}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPDropdown
                    label={t(COMPANY_INFORMATION__COMPANY_FORMAT)}
                    value={field.value}
                    placeholder="vb. BV"
                    onChange={field.onChange}
                    items={companyFormatOptions}
                    required
                    error={!!errors.companyFormat}
                    helperText={getInputErrorText(errors, CompanyStepFieldNames.COMPANY_FORMAT)}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
        </OnboardingFlowCardContentContainer>
        {processFinishedError && <OnboardingCardError>{t(ONBOARDING__PROCESS_ALREADY_FINISHED)}</OnboardingCardError>}
        <OnboardingFlowCardActionsContainer>
          <UPButton text={t(ONBOARDING__NEXT_STEP)} onClick={onSubmit} disabled={loading} />
        </OnboardingFlowCardActionsContainer>
      </OnboardingFlowCard>
    </OnboardingFlowStepContainer>
  );
};

export default CompanyStep;
